<!-- FileModal.vue -->
<!--
各応募者に添付された様々なファイル形式（画像、PDF、Officeドキュメント、テキスト）のプレビューを表示するモーダルウィンドウを実装しています。
主な機能：
- ファイルのプレビュー表示
- ダウンロードと印刷機能
- 画像のズームとローテーション
- レスポンシブデザイン
-->

<template>
  <div v-if="isOpen" class="modal" @click.self="close">
    <div class="modal-content">
      <!-- モーダルヘッダー：ファイル名と操作ボタンを表示 -->
      <div class="modal-header">
        <h2>{{ file.name }}</h2>
        <div class="modal-actions">
          <!-- ダウンロード、印刷、ズーム、回転などの操作ボタン -->
          <button @click="download($event)" class="btn btn-primary">
            <i class="mdi mdi-download"></i> ダウンロード
          </button>
          <button @click="print" class="btn btn-secondary">
            <i class="mdi mdi-printer"></i> 印刷
          </button>
          <button v-if="canZoom" @click="zoomIn" class="btn btn-secondary">
            <i class="mdi mdi-magnify-plus"></i>
          </button>
          <button v-if="canZoom" @click="zoomOut" class="btn btn-secondary">
            <i class="mdi mdi-magnify-minus"></i>
          </button>
          <button v-if="isImage" @click="rotateImage" class="btn btn-secondary">
            <i class="mdi mdi-rotate-right"></i>
          </button>
          <button @click="close" class="btn btn-close" aria-label="閉じる">&times;</button>
        </div>
      </div>
      <!-- モーダルボディ：ファイルプレビューを表示 -->
      <div class="modal-body card-body">
        <div v-if="loading" class="loader">読み込み中...</div>
        <div v-else-if="error" class="error">{{ error }}</div>
        <div v-else class="file-preview-container" :style="containerStyle">
          <!-- 画像プレビュー -->
          <img v-if="isImage" :src="file.file" :alt="file.name" class="file-preview" 
               :style="previewStyle" @load="onImageLoad" @error="onImageError">
          
          <!-- PDF プレビュー -->
          <iframe v-else-if="isPDF" :src="file.file" class="file-preview pdf-preview" 
                  :style="pdfPreviewStyle"></iframe>
          
          <!-- Office ドキュメントプレビュー -->
          <iframe v-else-if="isOfficeDoc" :src="officeViewerUrl" class="file-preview office-preview" 
                  :style="officePreviewStyle"></iframe>
          
          <!-- テキストファイルプレビュー -->
          <pre v-else-if="isText" class="file-preview text-content" :style="previewStyle">{{ fileContent }}</pre>
          
          <!-- その他のファイル形式 -->
          <div v-else class="unsupported-file">
            このファイル形式はプレビューに対応していません。ダウンロードしてご確認ください。
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: ['file', 'isOpen'],
  data() {
    return {
      loading: true,
      error: null,
      fileContent: null,
      fileUrl: null,
      zoom: 1,
      rotation: 0
    }
  },
  computed: {
    // プレビューのスタイル（ズームと回転を適用）
    previewStyle() {
      return {
        transform: `scale(${this.zoom}) rotate(${this.rotation}deg)`,
        transition: 'transform 0.2s ease-out'
      };
    },
    // ファイル拡張子を取得
    fileExtension() {
      return this.file.name.split('.').pop().toLowerCase();
    },
    // ファイルタイプの判定
    isImage() {
      return ['jpg', 'jpeg', 'png', 'gif', 'tiff'].includes(this.fileExtension);
    },
    isPDF() {
      return this.fileExtension === 'pdf';
    },
    isOfficeDoc() {
      return ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(this.fileExtension);
    },
    isText() {
      return ['txt', 'csv', 'json'].includes(this.fileExtension);
    },
    // ズーム機能の有効/無効
    canZoom() {
      return this.isImage;
    },
    // プレビューコンテナのスタイル
    containerStyle() {
      return {
        maxWidth: '100%',
        maxHeight: '70vh',
        overflow: 'auto'
      };
    },
    // PDF表示用のURL
    pdfViewerUrl() {
      return `https://mozilla.github.io/pdf.js/web/viewer.html?file=${encodeURIComponent(this.file.file)}`;
    },
    // Officeドキュメント表示用のURL
    officeViewerUrl() {
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(this.file.file)}`;
    },
    // PDFプレビューのスタイル
    pdfPreviewStyle() {
      return {
        width: '100%',
        height: '70vh',
        border: 'none'
      };
    },
    // Officeドキュメントプレビューのスタイル
    officePreviewStyle() {
      return {
        width: '100%',
        height: '70vh',
        border: 'none'
      };
    }
  },
  methods: {
    // モーダルを閉じる
    close() {
      this.$emit('close');
    },
    // ファイルをダウンロード
    async download(event) {
      // Ctrlキー（Macの場合はCommandキー）が押されているかチェック
      const isCtrlClick = event.ctrlKey || event.metaKey;

      try {
        // 通常のダウンロード処理
        if (!isCtrlClick) {
          const response = await fetch(this.file);
          if (!response.ok) throw new Error('ダウンロードに失敗しました');
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.style.display = 'none';
          a.href = this.file.file;
          a.download = this.file.file;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(this.file.file);
        } else {
          // 新しいタブでファイルを開く
          window.open(this.file.file, '_blank');
        }
      } catch (error) {
        this.error = error.message;
      }
    },
    // 印刷機能
    print() {
      window.print();
    },
    // ファイル内容を読み込む（テキストファイル用）
    async loadFileContent() {
      this.loading = true;
      this.error = null;
      try {
        if (this.isText) {
          const response = await fetch(this.file.file);
          if (!response.ok) throw new Error('ファイルの読み込みに失敗しました');
          this.fileContent = await response.text();
        }
        this.loading = false;
      } catch (error) {
        this.error = error.message;
        this.loading = false;
      }
    },
    // 画像読み込み完了時の処理
    onImageLoad() {
      this.loading = false;
    },
    // 画像読み込みエラー時の処理
    onImageError() {
      this.error = '画像の読み込みに失敗しました';
      this.loading = false;
    },
    // ズームイン
    zoomIn() {
      this.zoom = Math.min(this.zoom + 0.1, 3);
    },
    // ズームアウト
    zoomOut() {
      this.zoom = Math.max(this.zoom - 0.1, 0.1);
    },
    // 画像を回転
    rotateImage() {
      this.rotation = (this.rotation + 90) % 360;
    }
  },
  watch: {
    // モーダルが開かれたときにファイル内容を読み込む
    isOpen(newVal) {
      if (newVal && this.file) {
        this.loadFileContent();
      }
    }
  },
  mounted() {
    // コンポーネントがマウントされたときにファイル内容を読み込む
    if (this.isOpen && this.file) {
      this.loadFileContent();
    }
  }
}
</script>

<style lang="scss" scoped>
// スタイルは変更なし
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  max-width: 80%;
  max-height: 80%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  padding: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #ccc;

  h2 {
    margin: 0;
    font-size: 18px;
  }
}

.modal-actions {
  button {
    margin-left: 10px;
  }
}

.modal-body {
  flex-grow: 1;
  overflow: auto;
  padding: 15px;
}

.file-preview-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.file-preview {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.text-content {
  white-space: pre-wrap;
  word-wrap: break-word;
  max-width: 100%;
  max-height: 100%;
  overflow: auto;
}

.loader, .error, .unsupported-file {
  text-align: center;
  padding: 20px;
}

.error {
  color: red;
}

// 既存のスタイルとの統合
.card-body {
  height: 60vh;
}

.btn {
  padding: 8px 15px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;

  &.btn-primary {
    background-color: #007bff;
    color: white;
  }

  &.btn-secondary {
    background-color: #6c757d;
    color: white;
  }

  &.btn-close {
    background-color: transparent;
    font-size: 20px;
    padding: 0;
    width: 30px;
    height: 30px;
  }
}
</style>


// WEBPACK FOOTER //
// src/components2/Manage/modal/FileModal.vue